/**
 * Hours-of-operation tab of the Location detail (DNKADM-0080) — the default/first
 * tab at `/locations/:locationId/hours`.
 *
 * Re-houses the standalone Hours-of-operation screen's load + error/Retry +
 * key-remount pattern, driven by the **route `locationId`** (no location selector —
 * the location is fixed by the detail it sits in). Loads the owned location's weekly
 * schedule (`getOperatingHours`) + date exceptions (`getHoursExceptions`) and hands
 * each freshly loaded view to the **unchanged** {@link OperatingHoursGrid} /
 * {@link HoursExceptionsEditor} (keyed so the rows reseed on each (re)load). Times are
 * the location's local time, `HH:mm`; `dayOfWeek` is 0=Sunday … 6=Saturday (the
 * editors own this). Gated by the detail's `RequireAdmin` route — no re-gate here.
 */
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Skeleton, Typography } from '@mui/material';
import { useResource } from '@/hooks/useResource';
import { useToast } from '@/components/useToast';
import { getHoursExceptions, getOperatingHours } from '@/api/operatingHours';
import { OperatingHoursGrid } from '@/features/operating-hours/OperatingHoursGrid';
import { HoursExceptionsEditor } from '@/features/operating-hours/HoursExceptionsEditor';
import { ScreenError } from '@/components/ScreenError';

export function LocationHoursTab() {
  const showToast = useToast();
  const { locationId = '' } = useParams<{ locationId: string }>();

  // The weekly hours + date exceptions for THIS location (the route param). Both
  // re-fetch when the route id changes.
  const hours = useResource(
    useCallback(() => getOperatingHours(locationId), [locationId]),
    [locationId],
  );
  const exceptions = useResource(
    useCallback(() => getHoursExceptions(locationId), [locationId]),
    [locationId],
  );

  const anyError = hours.status === 'error' || exceptions.status === 'error';
  const anyLoading = hours.status === 'loading' || exceptions.status === 'loading';
  const ready =
    hours.status === 'ready' &&
    hours.data &&
    exceptions.status === 'ready' &&
    exceptions.data;

  return (
    <Box>
      {anyError && (
        <ScreenError
          error={hours.status === 'error' ? hours.error : exceptions.error}
          what="the operating hours"
          onRetry={() => {
            hours.refetch();
            exceptions.refetch();
          }}
          sx={{ maxWidth: 620, alignItems: 'center' }}
        />
      )}

      {!anyError && anyLoading && (
        <Skeleton
          variant="rounded"
          height={420}
          role="status"
          aria-label="Loading operating hours"
          sx={{ borderRadius: 3, maxWidth: 640 }}
        />
      )}

      {!anyError && ready && hours.data && exceptions.data && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Weekly hours
          </Typography>
          <OperatingHoursGrid
            // Remount on each successful (re)load so the rows reseed from the freshly
            // loaded view.
            key={`hours|${locationId}|${hours.data.days
              .map(
                (d) =>
                  `${d.dayOfWeek}:${
                    d.isAvailable
                      ? d.shifts.map((s) => `${s.startTime}-${s.endTime}`).join(',')
                      : 'x'
                  }`,
              )
              .join('|')}`}
            days={hours.data.days}
            locationId={locationId}
            refetch={hours.refetch}
            showToast={showToast}
          />

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            Date exceptions
          </Typography>
          <HoursExceptionsEditor
            key={`ex|${locationId}|${exceptions.data
              .map(
                (e) =>
                  `${e.exceptionDate}:${
                    e.isAvailable
                      ? e.shifts.map((s) => `${s.startTime}-${s.endTime}`).join(',')
                      : 'x'
                  }`,
              )
              .join('|')}`}
            exceptions={exceptions.data}
            locationId={locationId}
            refetch={exceptions.refetch}
            showToast={showToast}
          />
        </>
      )}
    </Box>
  );
}
